import {
    CheckLoginResultData,
    IAuthClient,
    IChangePasswordParams,
    ICheckLoginParams,
    ILoginCredentials,
    LoginResultData,
} from "@multiweb/clients/auth";
import { LoginResult } from "@multiweb/types/contracts/auth";
import { HeadersBuilder } from "Global/Base/Comunication/HeadersBuilder";
import { HTTPRequest } from "Global/Base/Comunication/HTTPRequest";
import { CancellationToken } from "Global/Base/Promises/CancellationTokenSource";
import { ASServices } from "OW.Remoting/Basic/ServiceEndpoints";
import { ICommunicationManager } from "OW.Remoting/Remoting/ICommunicationManager";
import { getCancellationTokenAsRequestContext } from "../../../RequestContext";
import { BaseClient } from "../BaseClient";
import { SESSION_ERROR_RESPONSE_CODES, SESSION_RESET_RESPONSE_CODE } from "./types";

/**
 * Není použita anotace "internal", protože implementace se používá v Samples.
 */
export abstract class AuthClient extends BaseClient implements IAuthClient {
    private readonly _webServiceUrl: string;
    private readonly _useLegacyApi?: boolean;

    /**
     * @param webServiceUrl Adresa webové služby, se kterou se má komunikovat
     * @param useLegacyApi Příznak určující zda se má komunikovat se starším rozhraním webové služby
     */
    constructor(communicationManager: ICommunicationManager, webServiceUrl: string, useLegacyApi?: boolean) {
        super(communicationManager);
        this._webServiceUrl = webServiceUrl;
        this._useLegacyApi = useLegacyApi;
    }

    public abstract login(credentials: ILoginCredentials, cancellationToken: CancellationToken): Promise<LoginResultData>;
    public abstract changeExpiredPassword(params: IChangePasswordParams, cancellationToken: CancellationToken): Promise<void>;

    public redirectToLoginPage(clientCode: string, queryString?: string): void {
        const url = this._useLegacyApi ? new URL("login", location.href) : new URL(`${this._webServiceUrl}/api/v02/as/auth/login`);

        if (!this._useLegacyApi)
            url.searchParams.append("clientName", clientCode);

        const searchParams = new URLSearchParams(queryString);
        searchParams.forEach((value, key) => url.searchParams.append(key, value));

        location.assign(url.href);
    }

    public async loginAnonymously(language: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<string> {
        return await this._communicationManager.callGETRequest("Login", ASServices.owManager, { language: language }, undefined, getCancellationTokenAsRequestContext(cancellationToken));
    }

    public async logout(clientCode: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<string> {
        if (this._useLegacyApi) {
            await this._communicationManager.callPOSTRequest("Logout", ASServices.owManager, undefined, undefined, undefined, getCancellationTokenAsRequestContext(cancellationToken));
            location.assign("logout");
        } else {
            location.assign(`${this._webServiceUrl}/api/v02/as/auth/logout?clientName=${clientCode}`);
        }
        return "";
    }

    public async checkLogin(params: ICheckLoginParams, cancellationToken: CancellationToken = CancellationToken.none): Promise<CheckLoginResultData> {
        const result = await this._checkLogin(params, cancellationToken);

        if (result.startsWith("<!DOCTYPE html")) // z nějakého důvodu když server vrátí chybovou stránku se statusem 401, tak v js je status pořád 200 (OK)
            throw new Error("CheckLogon failed. Server responded with error html page.");

        for (const status of SESSION_ERROR_RESPONSE_CODES) {
            if (result.startsWith(status))
                return { isLoggedIn: false, authId: null };
        }

        if (result === SESSION_RESET_RESPONSE_CODE)
            return await this.checkLogin(params, cancellationToken);

        const pair = result.split("\n")[1];
        const authId = pair.split("|")[1];

        return {
            isLoggedIn: true,
            authId,
        };
    }

    protected _checkLogin(params: ICheckLoginParams, cancellationToken: CancellationToken = CancellationToken.none): Promise<string> {
        const handlerUrl = `${this._webServiceUrl}${this._useLegacyApi ? "/handlers/auth/CheckLogon.aspx" : "/api/v01/as/auth/check"}?winAuth=${params.winAuth}&sso=${params.sso}`;

        return HTTPRequest.request("POST", handlerUrl, undefined, undefined, undefined, undefined, cancellationToken);
    }

    //#region Serializers, Deserializers
    protected _deserializeLoginResult(jsonLoginResultObject: number): LoginResult {
        return jsonLoginResultObject;
    }
    //#endregion Serializers, Deserializers

    //#region Soap
    protected _buildSoapRequestData(functionName: string, functionBody: string): string {
        return `<?xml version="1.0" encoding="utf-8"?>
        <soap12:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:soap12="http://www.w3.org/2003/05/soap-envelope">
          <soap12:Body>
            <${functionName} xmlns="http://tescosw.cz/">
              ${functionBody}
            </${functionName}>
          </soap12:Body>
        </soap12:Envelope>`;
    }

    protected async _postSoapOnLogonService(functionName: string, functionBody: string, cancellationToken: CancellationToken): Promise<any> {
        const xmlData = this._buildSoapRequestData(functionName, functionBody);
        const headers = new HeadersBuilder({ "Content-Type": "application/soap+xml; charset=utf-8", "SOAPAction": `http://tescosw.cz/${functionName}` });
        return await HTTPRequest.request("POST", `${this._webServiceUrl}/LogonService.asmx?op=${functionName}`, xmlData, headers, undefined, undefined, cancellationToken);
    }
    //#endregion Soap

    //#region XmlUtilityMethods
    protected _parseXml(xml: string): Document {
        return new DOMParser().parseFromString(xml, "text/xml");
    }

    protected _getAttributeValue(xml: string, attribute: string): string {
        return this._parseXml(xml).getElementsByTagName(attribute)[0].innerHTML;
    }
    //#endregion XmlUtilityMethods
}
